// /expat-relocation/$topic — TH detail page. Phase P-EXPAT-RELOCATION.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ExpatTopic, ExpatCategory } from "@/data/expat-relocation";
import { EXPAT_TOPICS_LITE, EXPAT_LITE_BY_CATEGORY, EXPAT_CAT_LABELS_TH, EXPAT_CAT_LABELS_EN } from "@/data/expat-index.gen";
import { EXPAT_CITIES } from "@/data/expat-cities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, AlertTriangle, FileText, Clock, Wallet, ShieldCheck, ChevronRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/expat-relocation/$topic/")({
  loader: async ({ params }) => {
    const { EXPAT_TOPICS } = await import("@/data/expat-relocation");
    const topic = EXPAT_TOPICS.find((t) => t.slug === params.topic);
    if (!topic) throw notFound();
    return { topic };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const t = loaderData.topic;
    const url = `${SITE}/expat-relocation/${t.slug}`;
    const title = `${t.titleTh} — ค่าใช้จ่าย เอกสาร ขั้นตอน 2026 | NYC`;
    const desc = t.quickAnswerTh.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, "/expat-relocation/$topic", "th") }, { name: "description", content: ctaDesc(desc, "/expat-relocation/$topic", "th")},
        { property: "og:title", content: ctaTitle(title, "/expat-relocation/$topic", "th")}, { property: "og:description", content: ctaDesc(desc, "/expat-relocation/$topic", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/expat-relocation/${t.slug}`, `/en/expat-relocation/${t.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: t.titleTh, serviceType: t.titleEn, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              ...(t.servicePriceTHB > 0 && { offers: { "@type": "Offer", price: String(t.servicePriceTHB), priceCurrency: "THB", description: `บริการ ${t.titleTh}` } }),
            }),
          },
          howToSchema({
            name: `วิธี${t.titleTh}`,
            description: t.taglineTh,
            totalTime: "P7D",
            estimatedCost: { currency: "THB", value: String(t.servicePriceTHB || 0) },
            inLanguage: "th",
            steps: t.stepsTh,
          }),
          faqPageSchema(t.faqsTh),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Expat Relocation", item: `${SITE}/expat-relocation` },
                { "@type": "ListItem", position: 3, name: t.titleTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบหัวข้อนี้</h1>
      <Link to="/expat-relocation" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">กลับไปหน้ารวม</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">เกิดข้อผิดพลาด</h1>
      <Link to="/expat-relocation" className="mt-4 inline-block text-primary">กลับไปหน้ารวม</Link>
    </main>
  ),
  component: ExpatTopicTh,
});

function ExpatTopicTh() {
  const { topic: t } = Route.useLoaderData() as { topic: ExpatTopic };
  const others = EXPAT_TOPICS_LITE.filter((x) => x.slug !== t.slug).slice(0, 6);
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/expat-relocation" className="hover:text-primary">Expat Relocation</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{t.titleTh}</span>
          </nav>
          <div className="flex items-center gap-3 text-4xl">{t.icon}</div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{t.titleTh}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{t.taglineTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{t.quickAnswerTh}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ค่าใช้จ่าย</div>
              <div className="text-sm font-bold">{t.costTh}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Clock className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ระยะเวลา</div>
              <div className="text-sm font-bold">{t.timelineTh}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <FileText className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">เอกสาร</div>
              <div className="text-sm font-bold">{t.docsTh.length} รายการ</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold">ขั้นตอน ({t.stepsTh.length} step)</h2>
              <ol className="mt-4 space-y-3">
                {t.stepsTh.map((s, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <div>
                        <div className="font-bold">{s.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">เอกสารที่ต้องเตรียม</h2>
              <ul className="mt-4 space-y-2">
                {t.docsTh.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">คำถามที่พบบ่อย</h2>
              <div className="mt-4 space-y-3">
                {t.faqsTh.map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4">
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5">
              <h3 className="flex items-center gap-2 font-bold text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" /> ข้อควรระวัง
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.warnTh}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">อ้างอิงกฎหมาย</h3>
              <p className="mt-2 text-xs text-muted-foreground">{t.refTh}</p>
            </div>
            {t.servicePriceTHB > 0 && (
              <div className="rounded-2xl border border-primary bg-primary/5 p-5">
                <h3 className="font-bold">บริการของ NYC</h3>
                <p className="mt-2 text-2xl font-extrabold text-primary">฿{t.servicePriceTHB.toLocaleString()}</p>
                <p className="mt-1 text-xs text-muted-foreground">รวมเอกสาร + ติดต่อหน่วยงาน + รับ-ส่ง</p>
                <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                  <Phone className="h-4 w-4" /> โทรปรึกษา
                </a>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><ShieldCheck className="mr-2 inline h-5 w-5 text-primary" />หัวข้อ Expat อื่น</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to="/expat-relocation/$topic" params={{ topic: o.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                <div className="flex items-center gap-2"><span className="text-xl">{o.icon}</span><span className="font-bold text-sm">{o.titleTh}</span></div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{o.taglineTh}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดู <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold">{t.titleTh} แยกตามเมือง</h2>
          <p className="mt-1 text-sm text-muted-foreground">ราคา เคล็ดลับ และคำแนะนำเฉพาะแต่ละเมืองยอดนิยมสำหรับเอ็กซ์แพท</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {EXPAT_CITIES.map((c) => (
              <Link key={c.slug} to="/expat-relocation/$topic/$city" params={{ topic: t.slug, city: c.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                <div className="flex items-center gap-2"><span className="font-bold text-sm">{t.titleTh} ใน{c.nameTh}</span></div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{c.categories[t.category].costNoteTh}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดู <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
